import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

export default function Cookies() {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("fe_cookie_consent") || "null");
      if (stored) {
        setAnalytics(!!stored.analytics);
        setMarketing(!!stored.marketing);
      }
    } catch {}
  }, []);

  const save = () => {
    localStorage.setItem(
      "fe_cookie_consent",
      JSON.stringify({ necessary: true, analytics, marketing, ts: Date.now() })
    );
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16">
      <div className="overline text-safety">Legal</div>
      <h1 className="mt-2 font-display font-black text-4xl lg:text-5xl tracking-tight text-forest leading-none">
        Cookie Policy
      </h1>
      <p className="mt-4 text-sm text-muted-foreground">Last updated: 24 August 2026</p>

      <div className="mt-10 space-y-8 text-base leading-relaxed text-foreground/90">
        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">1. What are cookies?</h2>
          <p>
            Cookies are small text files placed on your device when you visit a website. Furniture
            Exchange, operated by Corporate Office Furniture Ltd, uses cookies and similar
            technologies (such as local storage) in line with the Privacy and Electronic
            Communications Regulations (PECR) and the UK GDPR.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">2. Types of cookies we use</h2>
          <ul className="list-disc pl-6 mt-2 space-y-2">
            <li>
              <strong>Strictly necessary cookies</strong> – required for the website to function,
              such as keeping you logged in and remembering items in your basket. These cannot be
              switched off as the site cannot work properly without them.
            </li>
            <li>
              <strong>Analytics cookies</strong> – help us understand how visitors use the site so
              we can improve it. These are only set with your consent.
            </li>
            <li>
              <strong>Marketing cookies</strong> – used to measure the effectiveness of
              advertising and, where you have consented, to tailor communications from Furniture
              Exchange and Corporate Office Furniture Ltd. These are only set with your consent.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">3. Manage your cookie preferences</h2>
          <p className="mb-4">
            You can update your preferences at any time using the controls below. Strictly
            necessary cookies are always on because the site cannot function without them.
          </p>

          <div className="space-y-4 border border-border rounded-none p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display font-medium">Strictly necessary</div>
                <div className="text-sm text-muted-foreground">Always active. Required for core site functionality.</div>
              </div>
              <input type="checkbox" checked disabled className="mt-1 h-5 w-5" />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display font-medium">Analytics</div>
                <div className="text-sm text-muted-foreground">Helps us understand site usage to improve the service.</div>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-1 h-5 w-5"
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display font-medium">Marketing</div>
                <div className="text-sm text-muted-foreground">Used for tailored offers from Furniture Exchange and Corporate Office Furniture Ltd.</div>
              </div>
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-1 h-5 w-5"
              />
            </div>

            <button
              onClick={save}
              className="mt-2 rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-6 font-display"
            >
              Save preferences
            </button>
            {saved && <span className="ml-3 text-sm text-safety">Preferences saved.</span>}
          </div>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">4. Third-party cookies</h2>
          <p>
            Some cookies may be set by third parties we work with, such as Stripe (payments) and
            Google (sign-in). These providers have their own privacy and cookie policies which we
            encourage you to review.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">5. Browser controls</h2>
          <p>
            Most browsers let you control or delete cookies through their settings. Blocking all
            cookies may affect the functionality of this and other websites.
          </p>
        </section>

        <section>
          <h2 className="font-display font-bold text-xl text-forest mb-2">6. More information</h2>
          <p>
            For details on how we use personal data more broadly, see our{" "}
            <Link to="/privacy" className="text-safety hover:underline">Privacy Policy</Link>. If
            you have questions about this Cookie Policy, email{" "}
            <a href="mailto:info@furnitureexchange.co.uk" className="text-safety hover:underline">
              info@furnitureexchange.co.uk
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
}
